/**
 * Выбор посетителя о счётчиках: «Принять» или «Отказаться».
 *
 * Один выбор на все счётчики сайта: Google Analytics и OpenPanel (16.10.2026,
 * решение владельца; Яндекс Метрики на этих сайтах нет). Касается только сайтов не для
 * России: aisearch.tech, ingdg.com, datahunter.store. На aisearch.ru и
 * других российских доменах счётчики работают как прежде, без плашки
 * (уведомительный режим 152-ФЗ, см. `lib/ais-cookie.ts`).
 *
 * Хранится в localStorage сайта, а не в cookie: на сервер выбор не нужен,
 * а лишняя cookie — это ещё одна строка в политике и ещё один заголовок
 * на каждом запросе. Нет записи (или хранилище закрыто, как в приватном
 * режиме) — выбора нет, плашка показывается.
 */

export type SoglasieGa4 = 'granted' | 'denied';

/** Ключ выбора. Имя читаемое: его видно в инструментах разработчика. */
export const SOGLASIE_GA4_KLYUCH = 'ga4_soglasie';

/** Ключ флага отладки (`?ga_debug=1`). */
export const OTLADKA_GA4_KLYUCH = 'ga4_otladka';

export { SOBYTIE_GOTOV, SOBYTIE_NASTROJKI } from '@/lib/soglasie-sobytiya';

declare global {
  interface Window {
    /** Счётчики, ждущие «Принять»: их кладёт встроенный скрипт `poSoglasiyu`. */
    __poSoglasiyu?: (() => void)[];
  }
}

/**
 * Выбор, сделанный на этой вкладке, когда хранилище закрыто (приватный
 * режим): действует до перезагрузки страницы, и это честно.
 */
let vPamyati: SoglasieGa4 | null = null;

export function prochitatSoglasie(): SoglasieGa4 | null {
  try {
    const v = window.localStorage.getItem(SOGLASIE_GA4_KLYUCH);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return vPamyati;
  }
}

export function zapisatSoglasie(v: SoglasieGa4): void {
  vPamyati = v;
  try {
    window.localStorage.setItem(SOGLASIE_GA4_KLYUCH, v);
  } catch {
    // Приватный режим: выбор остаётся в `vPamyati` до конца страницы.
  }
}

/** Российский домен по имени хоста. Та же граница, что в `lib/ga4-id.ts`. */
const RU_HOST = '/(^|\\.)(ru|рф|xn--p1ai)$/';

/**
 * Встроенный скрипт счётчика, который ждёт согласия.
 *
 * Код счётчика заворачивается в функцию и запускается:
 * - сразу на российском домене — там всё как раньше, без плашки;
 * - сразу, если посетитель уже нажал «Принять»;
 * - иначе кладётся в `window.__poSoglasiyu` и ждёт «Принять» на плашке
 *   (`zapustitOtlozhennye`). «Отказаться» его не запускает никогда: до
 *   нового выбора в «Настройках cookie».
 *
 * Пока функция не запущена, счётчик не загружается и ничего не ставит.
 */
export function poSoglasiyu(kod: string): string {
  return `(function(){var z=function(){${kod}\n};if(${RU_HOST}.test(location.hostname)){z();return;}var s=null;try{s=localStorage.getItem(${JSON.stringify(SOGLASIE_GA4_KLYUCH)});}catch(e){}if(s==='granted'){z();return;}(window.__poSoglasiyu=window.__poSoglasiyu||[]).push(z);})();`;
}

/** Запустить счётчики, ждавшие согласия. Каждый — один раз. */
export function zapustitOtlozhennye(): void {
  const ochered = window.__poSoglasiyu ?? [];
  window.__poSoglasiyu = [];
  for (const z of ochered) {
    try {
      z();
    } catch {
      // Сломанный счётчик не должен мешать остальным.
    }
  }
}

/** Имена cookie Google Analytics. Метрики на этих сайтах нет совсем (16.10.2026). */
const COOKIE_SCHYOTCHIKOV = /^(_ga|_ga_.+|_gid|_gat.*)$/;

/**
 * Отзыв согласия: стереть cookie счётчиков на этом домене и его родителе
 * (GA ставит их на `.aisearch.tech`).
 */
export function udalitSledySchyotchikov(): void {
  const host = window.location.hostname;
  const chasti = host.split('.');
  const domeny = ['', host, `.${host}`];
  if (chasti.length > 2) domeny.push(`.${chasti.slice(-2).join('.')}`);
  else domeny.push(`.${host}`);

  for (const para of document.cookie.split('; ')) {
    const imya = para.split('=')[0] ?? '';
    if (!COOKIE_SCHYOTCHIKOV.test(imya)) continue;
    for (const d of domeny) {
      document.cookie = `${imya}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`;
    }
  }
}
